import { resolveId } from '@medplum/core'
import type { Reference, Task } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { SDR } from '@seen/core/models/tasks/sdr/sdr'
import type { SDRSignatureTask } from '@seen/core/models/tasks/signature/sdrSignatureTask'
import { Button, TypographyOverline, TypographyPUI } from '@seen/ui'
import { IconCheck } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { Card } from '#src/components/core/Card.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

export function SDRCard({
  sdr: sdrRef,
  task: signatureTask,
}: {
  sdr: Reference<Task>
  task: SDRSignatureTask
}): ReactNode {
  const { data: sdr } = useRead(SDR, sdrRef)
  const currentPractitioner = useCurrentPractitioner()

  const patchMutation = usePatchMutation<SDRSignatureTask>()

  const isSigner =
    signatureTask.ownerPractitioner?.reference ===
    currentPractitioner.reference.reference

  const signSDR = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    invariant(sdr, 'SDR is required')
    invariant(isSigner, 'Only the signer can sign the SDR')
    patchMutation.mutate({
      instance: signatureTask,
      mutateFn: (task) => {
        task.sign(currentPractitioner.reference)
      },
    })
  }

  if (!sdr) {
    return <Loader />
  }

  return (
    <Link to={`/Patient/${resolveId(sdr.for)}/SDR/${sdr.id}`}>
      <Card className="flex flex-col gap-4 p-4 hover:bg-muted">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-10">
            <div className="flex flex-col gap-1">
              <TypographyOverline>SDR</TypographyOverline>
              <TypographyPUI>{sdr.title}</TypographyPUI>
            </div>
            <div className="flex flex-col gap-1">
              <TypographyOverline>Description</TypographyOverline>
              <TypographyPUI>{sdr.description}</TypographyPUI>
            </div>
            <div className="flex flex-col gap-1">
              <TypographyOverline>Created Date</TypographyOverline>
              <TypographyPUI>
                <HoverDate date={sdr.createdAt} />
              </TypographyPUI>
            </div>
            {sdr.decisionNotificationDate && (
              <div className="flex flex-col gap-1">
                <TypographyOverline>Due Date</TypographyOverline>
                <TypographyPUI>
                  <HoverDate date={sdr.decisionNotificationDate} />
                </TypographyPUI>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end">
          {signatureTask.isDone ? (
            <Button variant="ghost" color="green" disabled>
              <IconCheck className="mr-2 size-4" />
              Signed
            </Button>
          ) : (
            <Button onClick={signSDR} disabled={!isSigner}>
              Sign
            </Button>
          )}
        </div>
      </Card>
    </Link>
  )
}
